// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { BoostedSportsByCategories } from '../../constants/boostedOdds';
import { VoidReasonLabels } from '../../constants/history';
import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { type CardMode } from '../../types/common';
import type { VoidReason } from '../../types/history';
import { SportIds } from '../../types/sports';
import TimerContainer from '../DefaultTimer/DefaultTimerContainer';
import FlagIcon from '../FlagIcon';
import HistoryMatchFullTitleContainer from '../HistoryMatchFullTitle/HistoryMatchFullTitleContainer';
import SportIcon from '../SportIcon';

const SportWithTimer = [
  SportIds.SOCCER,
  SportIds.FUTSAL,
  SportIds.HANDBALL,
  SportIds.FIELD_HOCKEY,
  SportIds.RUGBY,
  SportIds.RUGBY_7S,
  SportIds.RUGBY_LEAGUE,
  SportIds.RUGBY,
  SportIds.WATERPOLO,
];

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.1em;
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;

  border-radius: 8px;

  ${({ $isBoosted, $cardMode }) =>
    $isBoosted
      ? css`
          background: ${theme.History.betslip.outcome.boostedBackground};
          padding: 0 0.25em;
          border: 1px solid ${theme.BoostedOddCard.badge[$cardMode]?.color};
          height: 1.5em;
        `
      : undefined}
`;

const StyledSportIcon = styled(SportIcon)`
  width: 1.55em;
  height: 1.55em;
`;

const StyledFlagIcon = styled(FlagIcon)`
  width: 1.07em;
  height: 1.07em;
  margin-left: 0.15em;
`;

const BoostedIcon = styled(StyledSportIcon)`
  ${({ $cardMode }) => css`
    color: ${theme.BoostedOddCard.badge[$cardMode]?.iconColor};
  `}
`;

const StyledHistoryMatchFullTitleContainer = styled(HistoryMatchFullTitleContainer)`
  flex: 1;

  ${({ $smallMargin }) =>
    $smallMargin
      ? css`
          padding-left: 0.4em;
        `
      : css`
          padding-left: 0.67em;
        `}
`;

const dateTimeStyle = css`
  font-size: 0.7em;
  text-align: right;
  margin: 0 0.5em;
`;

const DateValue = styled.div`
  ${dateTimeStyle}
  color: ${theme.History.outcome.match.dateColor};
  text-transform: capitalize;
`;

const VoidReasonWrapper = styled.div`
  color: white;
  background: ${theme.History.outcome.footer.voidBackgroundColor};
  border-radius: 7px;
  white-space: nowrap;
  padding: 0.17em 0.67em;
  font-size: 0.8em;

  margin-left: 0.5em;
`;

const StyledTimer = styled(TimerContainer)`
  ${dateTimeStyle}
  color: ${theme.History.outcome.match.timerColor};
`;

type HistoryItemOutcomeCardMatchProps = {
  className?: string;
  sportId: number;
  categoryId?: number;
  flagId?: string;
  matchId?: string;
  isLive: boolean | null;
  title?: string;
  startDate: number | null;
  isSettled?: boolean;
  voidReason?: VoidReason;
  isBoosted?: boolean;
  cardMode: CardMode;
};

const HistoryItemOutcomeCardMatch = ({
  className,
  sportId,
  categoryId,
  flagId,
  matchId,
  isLive,
  title,
  startDate,
  isSettled,
  voidReason,
  isBoosted,
  cardMode,
}: HistoryItemOutcomeCardMatchProps) => {
  const formattedDate = useMemo(() => Formatter.formatMatchStart(startDate, __), [startDate]);

  const voidReasonLabel = useMemo(() => (voidReason ? VoidReasonLabels[voidReason] : null), [voidReason]);

  const renderSportIcon = useCallback(() => {
    if (isBoosted) {
      return <BoostedIcon sportId={sportId} $cardMode={cardMode} />;
    }

    return <StyledSportIcon sportId={sportId} />;
  }, [isBoosted, sportId, cardMode]);

  const renderInfo = useCallback(() => {
    if (voidReasonLabel) {
      return <VoidReasonWrapper>{voidReasonLabel}</VoidReasonWrapper>;
    }
    if (isLive && ~SportWithTimer.indexOf(sportId)) {
      return <StyledTimer matchId={matchId} showPeriod />;
    }
    if (!isLive && !isSettled) {
      return <DateValue>{formattedDate}</DateValue>;
    }
  }, [voidReasonLabel, matchId, isLive, isSettled, formattedDate, sportId]);

  return (
    <Wrapper className={className}>
      <IconsWrapper $isBoosted={isBoosted} $cardMode={cardMode}>
        {renderSportIcon()}
        {BoostedSportsByCategories[categoryId] ? (
          <StyledSportIcon sportId={BoostedSportsByCategories[categoryId]} />
        ) : null}
        {flagId ? <StyledFlagIcon flagId={flagId} /> : null}
      </IconsWrapper>
      <StyledHistoryMatchFullTitleContainer
        matchId={matchId}
        title={title}
        sportId={sportId}
        categoryId={categoryId}
        hasVoidReason={!!voidReasonLabel}
        $smallMargin={!flagId}
      />
      {renderInfo()}
    </Wrapper>
  );
};

export default HistoryItemOutcomeCardMatch;
